<template>
  <div class="rounded-2xl border border-[#e2e8f0] bg-white overflow-hidden shadow-sm">
    <!-- Header -->
    <div class="bg-[#2d3748] text-white px-3.5 py-2 flex items-center gap-2">
      <Wrench class="w-4 h-4 flex-shrink-0" />
      <span class="font-medium text-sm">{{ $t('diy_title') }}</span>
    </div>

    <!-- Editable form -->
    <div v-if="!confirmed" class="p-3.5 space-y-3">
      <!-- Category -->
      <div>
        <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_category') }}</label>
        <select v-model="local.category" class="diy-input bg-white">
          <option v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </div>

      <!-- Fitting type -->
      <div>
        <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_fitting_type') }}</label>
        <input v-model="local.fittingType" type="text" class="diy-input" />
      </div>

      <!-- Sizes as chips -->
      <div>
        <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_sizes') }}</label>
        <div v-if="local.sizes.length" class="flex flex-wrap gap-1.5 mb-1.5">
          <span v-for="(s, i) in local.sizes" :key="i"
            class="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--color-primary-light)] text-[var(--color-primary)] rounded-full text-xs">
            {{ s }}
            <button type="button" @click="removeSize(i)" class="hover:text-[var(--color-primary-dark)]" :aria-label="$t('chat_remove')">
              <X class="w-3 h-3" />
            </button>
          </span>
        </div>
        <div class="flex gap-1.5">
          <input v-model="sizeInput" type="text" class="diy-input flex-1"
            @keydown.enter.prevent="addSize" />
          <button type="button" @click="addSize"
            class="px-2.5 py-1.5 rounded-lg bg-[var(--color-primary-light)] text-[var(--color-primary)] text-xs font-medium hover:bg-[#d5e6f7] transition-colors flex-shrink-0">
            {{ $t('diy_add_size') }}
          </button>
        </div>
      </div>

      <!-- Two-column detail inputs -->
      <div class="grid grid-cols-2 gap-2.5">
        <div>
          <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_pressure') }}</label>
          <input v-model="local.pressureClass" type="text" class="diy-input" />
        </div>
        <div>
          <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_standard') }}</label>
          <input v-model="local.standard" type="text" class="diy-input" />
        </div>
        <div>
          <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_material') }}</label>
          <input v-model="local.material" type="text" class="diy-input" />
        </div>
        <div>
          <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_color') }}</label>
          <input v-model="local.color" type="text" class="diy-input" />
        </div>
        <div>
          <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_quantity') }}</label>
          <input v-model="local.quantity" type="text" class="diy-input" />
        </div>
      </div>

      <!-- Notes -->
      <div>
        <label class="block text-[11px] font-medium text-[#4a5568] mb-1">{{ $t('diy_notes') }}</label>
        <textarea v-model="local.notes" rows="2" class="diy-input resize-y"></textarea>
      </div>

      <button type="button" @click="confirm"
        class="w-full py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-dark)] transition-colors flex items-center justify-center gap-1.5">
        <Check class="w-4 h-4" />{{ $t('diy_confirm') }}
      </button>
    </div>

    <!-- Frozen summary -->
    <div v-else class="p-3.5 space-y-3">
      <div class="flex items-center gap-1.5 text-green-600 text-xs font-medium">
        <CheckCircle class="w-4 h-4" />{{ $t('diy_saved') }}
      </div>
      <dl class="text-xs space-y-1">
        <div v-for="row in summaryRows" :key="row.label" class="flex gap-2">
          <dt class="text-[#718096] flex-shrink-0 w-24">{{ row.label }}</dt>
          <dd class="text-[#2d3748] font-medium break-words">{{ row.value }}</dd>
        </div>
      </dl>
      <div class="flex gap-2 pt-1">
        <button type="button" @click="confirmed = false"
          class="flex-1 py-2 rounded-lg border border-[#e2e8f0] text-[#4a5568] text-sm font-medium hover:bg-[#f7fafc] transition-colors flex items-center justify-center gap-1.5">
          <Pencil class="w-3.5 h-3.5" />{{ $t('diy_edit') }}
        </button>
        <button type="button" @click="attach"
          class="flex-1 py-2 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-dark)] transition-colors flex items-center justify-center gap-1.5">
          <FileText class="w-3.5 h-3.5" />{{ $t('diy_attach_quote') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Wrench, X, Check, CheckCircle, Pencil, FileText } from 'lucide-vue-next'
import { productsData } from '../../data/products'
import { attachDraft } from '../../services/quoteDraft'

const props = defineProps({
  draft: { type: Object, required: true }
})
const emit = defineEmits(['attached'])

const router = useRouter()
const { t, locale } = useI18n()

// Local editable copy — never mutate the message payload directly.
const local = reactive({
  id: props.draft.id || undefined,
  createdAt: props.draft.createdAt || new Date().toISOString(),
  locale: props.draft.locale || locale.value,
  category: props.draft.category || 'other',
  fittingType: props.draft.fittingType || '',
  sizes: Array.isArray(props.draft.sizes) ? [...props.draft.sizes] : [],
  pressureClass: props.draft.pressureClass || '',
  standard: props.draft.standard || '',
  material: props.draft.material || '',
  color: props.draft.color || '',
  quantity: props.draft.quantity || '',
  notes: props.draft.notes || ''
})

const confirmed = ref(false)
const sizeInput = ref('')

const categoryOptions = computed(() => {
  const opts = productsData.map((p) => ({ value: p.id, label: p.name[locale.value] || p.name.en }))
  opts.push({ value: 'other', label: t('diy_other') })
  return opts
})

function addSize() {
  const v = sizeInput.value.trim()
  if (v && local.sizes.length < 10) local.sizes.push(v.slice(0, 40))
  sizeInput.value = ''
}
function removeSize(i) {
  local.sizes.splice(i, 1)
}

function confirm() {
  confirmed.value = true
}

const summaryRows = computed(() => {
  const catLabel = categoryOptions.value.find((o) => o.value === local.category)
  const rows = [
    { label: t('diy_category'), value: catLabel ? catLabel.label : local.category },
    { label: t('diy_fitting_type'), value: local.fittingType },
    { label: t('diy_sizes'), value: local.sizes.join(', ') },
    { label: t('diy_pressure'), value: local.pressureClass },
    { label: t('diy_standard'), value: local.standard },
    { label: t('diy_material'), value: local.material },
    { label: t('diy_color'), value: local.color },
    { label: t('diy_quantity'), value: local.quantity },
    { label: t('diy_notes'), value: local.notes }
  ]
  return rows.filter((r) => r.value)
})

function buildDraft() {
  return {
    id: local.id,
    createdAt: local.createdAt,
    locale: local.locale,
    category: local.category,
    fittingType: local.fittingType,
    sizes: [...local.sizes],
    pressureClass: local.pressureClass || undefined,
    standard: local.standard || undefined,
    material: local.material || undefined,
    color: local.color || undefined,
    quantity: local.quantity || undefined,
    notes: local.notes || undefined
  }
}

function attach() {
  attachDraft(buildDraft())
  emit('attached')
  router.push('/quote')
}
</script>

<style scoped>
.diy-input {
  width: 100%;
  padding: 0.4rem 0.55rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.diy-input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-a10);
}
</style>
